import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  StudentImportCommitResponse,
  StudentImportPreviewResponse,
  StudentImportTemplateInfoResponse,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase C1c — IA student-import hooks.
 *
 * The wizard's import-students step calls these:
 *
 *  1. ``useStudentImportTemplateInfo`` — GET `/me/first-run/students/template`
 *     for the canonical column list + alias hints. Drives the "Allowed
 *     columns" pop-over without needing to round-trip the CSV bytes.
 *  2. ``useStudentImportPreview`` — multipart POST
 *     `/me/first-run/students/preview` for per-row verdicts before the
 *     principal commits. The preview is pure (no DB writes) so the
 *     principal can iterate on the file.
 *  3. ``useStudentImportCommit`` — multipart POST
 *     `/me/first-run/students/commit` to actually persist. ``skipDuplicates``
 *     defaults to true (partial success). false ⇒ BE returns 422
 *     IMPORT_HAS_DUPLICATES if any duplicate rows are present and writes
 *     nothing.
 *
 * The template-CSV download is a plain anchor href to
 * ``/me/first-run/students/template.csv`` — no hook needed.
 */

export function useStudentImportTemplateInfo() {
  return useQuery<StudentImportTemplateInfoResponse>({
    queryKey: ["student-imports", "template"],
    queryFn: () => api.get<StudentImportTemplateInfoResponse>("/me/first-run/students/template"),
    // The template metadata is effectively static (column list rarely
    // changes between phases) — a long staleTime keeps the wizard snappy
    // without round-trips on every focus.
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export interface PreviewVariables {
  file: File;
}

export function useStudentImportPreview() {
  return useMutation<StudentImportPreviewResponse, Error, PreviewVariables>({
    mutationKey: ["student-imports", "preview"],
    mutationFn: async ({ file }) => {
      const form = new FormData();
      form.append("file", file, file.name || "students.csv");
      return api.post<StudentImportPreviewResponse>("/me/first-run/students/preview", form);
    },
  });
}

export interface CommitVariables {
  file: File;
  skipDuplicates?: boolean;
}

export function useStudentImportCommit() {
  return useMutation<StudentImportCommitResponse, Error, CommitVariables>({
    mutationKey: ["student-imports", "commit"],
    mutationFn: async ({ file, skipDuplicates = true }) => {
      const form = new FormData();
      form.append("file", file, file.name || "students.csv");
      form.append("skip_duplicates", skipDuplicates ? "true" : "false");
      return api.post<StudentImportCommitResponse>("/me/first-run/students/commit", form);
    },
  });
}
